import { ReactNode, useEffect, useRef } from "react";

interface Props {
    title: string;
    subtitle?: string;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    width?: "narrow" | "wide";
}

/**
 * A dialog that behaves like one.
 *
 * Escape closes, focus moves inside on open and is trapped there, and the
 * backdrop is inert to clicks. Backdrop-to-dismiss is deliberately absent: every
 * modal in this app is a form, and losing a half-written incident report to a
 * stray click on the overlay is a real cost with no matching benefit.
 */
export const Modal = ({ title, subtitle, onClose, children, footer, width = "narrow" }: Props) => {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const panel = panelRef.current;

        const focusable = () =>
            Array.from(
                panel?.querySelectorAll<HTMLElement>(
                    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ) ?? []
            ).filter((el) => el.offsetParent !== null);

        focusable()[0]?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key !== "Tab") return;

            const items = focusable();
            if (items.length === 0) return;
            const first = items[0]!;
            const last = items[items.length - 1]!;

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", onKeyDown, true);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            document.body.style.overflow = "";
            previouslyFocused?.focus?.();
        };
    }, [onClose]);

    return (
        <>
            <div className="scrim" />
            <div
                className={`modal ${width}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                ref={panelRef}
            >
                <div className="modal-head">
                    <div>
                        <h2>{title}</h2>
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                    <button className="btn ghost small" onClick={onClose} aria-label="Close">
                        Close
                    </button>
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-foot">{footer}</div>}
            </div>
        </>
    );
};
